// src/components/AddJudgeDialog.js
import { API_URL } from '../config/apiConfig';
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { addCompetitionJudge } from '../api/competitionApi';
import axios from 'axios';


export default function AddJudgeDialog({ open, onClose, competitionId, existingJudges = [] }) {
    const [selectedUser, setSelectedUser] = useState(null);
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const searchUsers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setUserSearchResults([]);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/search-users`, {
                params: { q: searchTerm },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            // Filter out users who are already judges
            const existingJudgeIds = existingJudges.map(j => j.user.id);
            const filteredResults = response.data.filter(user => !existingJudgeIds.includes(user.id));
            
            setUserSearchResults(filteredResults);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    const handleSubmit = async () => {
        if (!selectedUser) {
            setErrorMessage('Please select a user');
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');
            await addCompetitionJudge(competitionId, selectedUser.id);
            setSelectedUser(null);
            setUserSearchResults([]);
            onClose();
        } catch (error) {
            console.error('Error adding judge:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to add judge');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedUser(null);
        setUserSearchResults([]);
        setErrorMessage('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Add Judge
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        Search for a user by name or email to add them as a judge for this competition.
                    </Typography>
                    
                    <Autocomplete
                        options={userSearchResults}
                        getOptionLabel={(option) => `${option.fullName || 'Unknown'} (${option.email})`}
                        value={selectedUser}
                        onChange={(e, value) => setSelectedUser(value)}
                        onInputChange={(e, value) => searchUsers(value)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search Users"
                                placeholder="Type at least 2 characters to search"
                                fullWidth
                                sx={{ mt: 2 }}
                            />
                        )}
                        noOptionsText="No users found"
                        loadingText="Searching..."
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !selectedUser}
                >
                    {loading ? 'Adding...' : 'Add Judge'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

